/**
 * Name matching for the recruitment interviewer lists that name a person as
 * "First name + first surname" (Client Care coordinators, the Revo QA lead).
 * Employee records carry both surnames ("Martin Moreno Madriz"), so a person
 * matches on the first name plus the first word of the last name, accents and
 * case ignored. Client-safe: no DB access.
 */
export function normalizeNamePart(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

export function firstWord(value: string | null | undefined): string {
  return normalizeNamePart(value).split(/\s+/)[0] ?? '';
}

function entryParts(entry: string): { first: string; surname: string } {
  const [first = '', surname = ''] = normalizeNamePart(entry).split(/\s+/);
  return { first, surname };
}

/** Employee record (first_name / last_name) is one of `entries`. */
export function employeeMatchesInterviewerEntries(
  entries: readonly string[],
  employee: { first_name?: string | null; last_name?: string | null },
): boolean {
  const first = firstWord(employee.first_name);
  const surname = firstWord(employee.last_name);
  if (!first || !surname) return false;
  return entries.some((entry) => {
    const parts = entryParts(entry);
    return parts.first === first && parts.surname === surname;
  });
}

/** Full name as stored on the interview ("Martin Moreno Madriz") is one of `entries`. */
export function nameMatchesInterviewerEntries(
  entries: readonly string[],
  name: string | null | undefined,
): boolean {
  const [first = '', surname = ''] = normalizeNamePart(name).split(/\s+/);
  if (!first || !surname) return false;
  return entries.some((entry) => {
    const parts = entryParts(entry);
    return parts.first === first && parts.surname === surname;
  });
}
